import { Popover } from "../../../../atoms/Popover"
import styles from "./styles.module.scss"
import { ModalCheckbox } from "../../../../atoms/ModalCheckbox"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo } from "react"
import SlidersIcon from "../../../../assets/icons/SlidersIcon"
import { UploadedFilesModal } from "../../../../modals/UploadedFilesModal"
import { getFilesWithMetadata } from "../../../../../services/storage/getNavigatorFiles"
import { useQuery } from "@tanstack/react-query"
import { TripID } from "../../../../../../libs/cruisen-contract/trips/trip.interface"
import { FileData } from "../../../../atoms/FileItem"
import FileTextOutline from "~icons/eva/file-text-outline"
import classNames from "classnames"
import { colors } from "@theme/colors"

type FileType = "confirmation" | "document" | "image"

class NavigatorFilesButtonStore {
  filesFiltersVisible = false
  fileTypes: FileType[] = []
  isHoveringFilters = false

  constructor() {
    makeAutoObservable(this)
  }

  toggleFilesFiltersVisible() {
    this.filesFiltersVisible = !this.filesFiltersVisible
  }

  toggleFileType(type: FileType) {
    this.fileTypes = this.fileTypes.includes(type)
      ? this.fileTypes.filter((t) => t !== type)
      : [...this.fileTypes, type]
  }

  clearFileTypes() {
    this.fileTypes = []
  }

  setIsHoveringFilters(value: boolean) {
    this.isHoveringFilters = value
  }
}

const filterFiles = (files: FileData[], fileTypes: FileType[]) => {
  if (fileTypes.length === 0) return files

  return files.filter((file) => fileTypes.includes(file.type))
}

const filtersTypes = [
  {
    label: "Confirmations",
    type: "confirmation",
  },
  {
    label: "Documents",
    type: "document",
  },
  {
    label: "Images",
    type: "image",
  },
] as const

export const NavigatorFilesButton = observer(
  ({
    activeTripId,
    setSelectedItem,
    isFilesModalVisible,
    toggleIsFilesModalVisible,
  }: {
    activeTripId: TripID
    setSelectedItem: (item: { isImage: boolean; url: string; name: string }) => void
    isFilesModalVisible: boolean
    toggleIsFilesModalVisible: () => void
  }) => {
    const state = useMemo(() => new NavigatorFilesButtonStore(), [])

    const { data: navigatorFiles } = useQuery({
      queryKey: ["navigatorFiles", activeTripId],
      queryFn: () => getFilesWithMetadata(activeTripId),
    })

    const filteredFiles = filterFiles(navigatorFiles, state.fileTypes)
    const availableTypes = filtersTypes.filter((type) =>
      navigatorFiles?.some((file) => file.type === type.type),
    )
    const shouldShowFilters = availableTypes.length > 1

    useEffect(() => {
      if (!isFilesModalVisible) {
        state.clearFileTypes()
      }
    }, [isFilesModalVisible])

    return (
      <div className={styles.headingButtons}>
        <Popover
          isOpen={isFilesModalVisible}
          wrapperClassName={!navigatorFiles?.length ? styles.noFiles : styles.modalContainer}
          onClickOutside={() => {
            if (state.filesFiltersVisible) return
            toggleIsFilesModalVisible()
          }}
          title="Files"
          align="end"
          headerRightElement={
            shouldShowFilters && (
              <Popover
                isOpen={state.filesFiltersVisible}
                onClickOutside={() => state.toggleFilesFiltersVisible()}
                wrapperClassName={styles.filtersPopoverWrapper}
                padding={14}
                align="end"
                title="Filters"
                content={
                  <div className={styles.fileFiltersContainer}>
                    {availableTypes.map((type) => (
                      <ModalCheckbox
                        key={type.type}
                        label={type.label}
                        isChecked={state.fileTypes.includes(type.type)}
                        onPress={() => state.toggleFileType(type.type)}
                      />
                    ))}
                  </div>
                }
              >
                <button
                  type="button"
                  className={classNames(
                    styles.filtersPopoverButton,
                    state.fileTypes.length > 0 && styles.filtersPopoverButtonActive,
                  )}
                  onClick={() => state.toggleFilesFiltersVisible()}
                  onMouseEnter={() => state.setIsHoveringFilters(true)}
                  onMouseLeave={() => state.setIsHoveringFilters(false)}
                >
                  <SlidersIcon
                    color={state.isHoveringFilters ? colors.iconSecondary : colors.iconPrimary}
                  />
                </button>
              </Popover>
            )
          }
          content={
            <UploadedFilesModal
              files={filteredFiles}
              handlePreview={(file: FileData) => {
                setSelectedItem({
                  isImage: file.thumbnail !== undefined,
                  url: file.fileURL,
                  name: file.fileName,
                })
              }}
              onClose={() => toggleIsFilesModalVisible()}
            />
          }
        >
          <button
            className={styles.uploadedFilesButton}
            onClick={() => toggleIsFilesModalVisible()}
          >
            <FileTextOutline />
            <p>{navigatorFiles?.length}</p>
          </button>
        </Popover>
      </div>
    )
  },
)
